import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { configureApiClient } from './services/api-client';
import { loadRuntimeConfig } from './services/runtime-config';
import { App } from './App';
import './styles/global.css';

/**
 * Wires the API client to the auth system.
 */
function ApiClientConfigurator({ children }: { children: React.ReactNode }) {
  const { getToken, logout } = useAuth();

  useEffect(() => {
    configureApiClient({
      getToken,
      onUnauthorized: () => {
        logout();
      },
    });
  }, [getToken, logout]);

  return <>{children}</>;
}

/**
 * Root component that loads config, configures Amplify, then renders the app.
 */
function Root() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadRuntimeConfig().then((config) => {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: config.cognitoUserPoolId,
            userPoolClientId: config.cognitoClientId,
            ...(config.cognitoDomain
              ? {
                  loginWith: {
                    oauth: {
                      domain: config.cognitoDomain,
                      scopes: config.oauthScopes ?? ['openid', 'email', 'profile'],
                      redirectSignIn: [config.redirectSignIn ?? window.location.origin + '/'],
                      redirectSignOut: [config.redirectSignOut ?? window.location.origin + '/'],
                      responseType: 'code' as const, // Req 6.3 auth-code + PKCE (Amplify manages PKCE + OAuth state, Req 10.1)
                    },
                  },
                }
              : {}),
          },
        },
      }, {
        ssr: false,
      });
      setReady(true);
    });
  }, []);

  if (!ready) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Loading...</div>;
  }

  return (
    <AuthProvider>
      <ApiClientConfigurator>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </ApiClientConfigurator>
    </AuthProvider>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
